/**
 * State and behaviour of the main window's tab strip (MainWindowTabs.tsx).
 *
 * TAB MODEL (hybrid). The "primary" (home) tab is the live app page,
 * rendered natively by App exactly as before (activeTab and its persistence are
 * untouched). It starts first and cannot be closed, so there is always a tab
 * and the page the user signed in to is never reloaded; it can be reordered
 * and pinned like any other tab. Every other tab is a
 * same-origin iframe of the page pop-out (/popout/page?tab=…), like a tab of a
 * tabbed pop-out window: mounted the first time it is shown, then kept mounted
 * (hidden) so its state survives switching. While an iframe tab is in front,
 * the native page stays mounted but hidden.
 *
 * NAVIGATION follows the tab in front: when an iframe tab is active, the
 * sidebar / rail / command palette / shortcuts call navigateActive(), which
 * tells that iframe to change page (postMessage 'weavein:navigate', validated
 * in the frame by lib/popout.ts parseHostNavigateMessage) instead of moving
 * the hidden primary page.
 *
 * PERSISTENCE: the extra tabs, the strip's order (where the home tab sits
 * too), how many are pinned and the active index are stored per user (lib/mainWindowTabs.ts) and restored on reload /
 * sign-in; signing out forgets them.
 *
 * REORDER / PIN / MOVE (TRUS-638): the order rules (pinned tabs first, else
 * free — the home tab included) are lib/tabStrip.ts; moving a tab to or from another window is
 * useTabMove + lib/tabMove.ts. The primary tab never leaves this window (it
 * can be duplicated or opened in a new window instead). An iframe tab that
 * moves in is a fresh iframe: its page reloads at the page it was showing.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type MutableRefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/components/ui/use-toast';
import {
  appTabKey,
  buildPagePopoutUrl,
  isValidTabId,
  openAppPopout,
  parseAppTabKey,
  registerInAppTabHandler,
  setMainTabHostFlag,
  stateFromKeys,
  type AppPopoutState,
  type AppTabSpec,
  type HostNavigateMessage,
  type OpenResult,
} from '@/lib/popout';
import { currentDesktopWindowLabel } from '@/lib/desktopWindow';
import { mainWindowId, type MoveTarget } from '@/lib/tabMove';
import {
  applyPinnedCount,
  moveTabBy,
  moveTabTo,
  moveTabToEdge,
  normalizeOrder,
  pinnedCount,
  setTabPinned,
  tabShortcutFromEvent,
  type TabDragPayload,
} from '@/lib/tabStrip';
import {
  MAX_MAIN_TABS,
  clearMainTabs,
  loadMainTabs,
  mainTabsStorageKey,
  saveMainTabs,
  type PersistedMainTabs,
} from '@/lib/mainWindowTabs';
import { useFrameBridge } from './useFrameBridge';
import { useHostAnnouncement, useTabMove, type MoveInResult, type TabMoveApi } from './useTabMove';

export const PRIMARY_UID = 'primary';

export interface MainTab {
  uid: string;
  /** Null for the primary tab: its page is App's own activeTab. */
  spec: AppTabSpec | null;
  /** What the iframe was opened at. */
  src?: string;
  /** Set by the page itself (e.g. a channel's name). */
  title?: string;
  /** False until first shown — a restored background tab costs nothing until then. */
  mounted: boolean;
  /** The page has reported in, so it is listening for navigate messages. */
  ready: boolean;
  /** Pinned tabs sit left of the others, drawn compact. */
  pinned: boolean;
}

interface StripState {
  /** Storage key this state belongs to; null until restored (nothing is saved before that). */
  owner: string | null;
  tabs: MainTab[];
  active: string;
}

const PRIMARY: MainTab = { uid: PRIMARY_UID, spec: null, mounted: true, ready: true, pinned: false };

let uidSeq = 0;
const nextUid = () => `m${++uidSeq}`;

function frameTab(spec: AppTabSpec, isDesktop: boolean, mounted: boolean, title?: string): MainTab {
  return { uid: nextUid(), spec, src: buildPagePopoutUrl(spec, { desktop: isDesktop }), mounted, ready: false, pinned: false, ...(title ? { title } : {}) };
}

function emptyStrip(owner: string | null = null): StripState {
  return { owner, tabs: [PRIMARY], active: PRIMARY_UID };
}

function fromPersisted(saved: PersistedMainTabs, owner: string, isDesktop: boolean): StripState {
  const frames = saved.tabs.map((spec) => frameTab(spec, isDesktop, false));
  const at = Math.max(0, Math.min(saved.primary ?? 0, frames.length));
  const placed = [...frames.slice(0, at), PRIMARY, ...frames.slice(at)];
  const active = placed[saved.active] ?? PRIMARY;
  const tabs = normalizeOrder(applyPinnedCount(mount(placed, active.uid), saved.pinned ?? 0));
  return { owner, tabs, active: active.uid };
}

function toPersisted(s: StripState): PersistedMainTabs {
  const frames = s.tabs.filter((t) => t.spec);
  const idx = s.tabs.findIndex((t) => t.uid === s.active);
  const primary = Math.max(0, s.tabs.findIndex((t) => t.uid === PRIMARY_UID));
  const pinned = pinnedCount(s.tabs);
  return {
    tabs: frames.map((t) => t.spec as AppTabSpec),
    active: idx < 0 ? 0 : idx,
    primary,
    ...(pinned ? { pinned } : {}),
  };
}

const mount = (tabs: MainTab[], uid: string) => tabs.map((t) => (t.uid === uid && !t.mounted ? { ...t, mounted: true } : t));

export interface UseMainWindowTabsOptions {
  /** This document is the main window (not a pop-out, not a framed page) and someone is signed in. */
  host: boolean;
  /** The strip is on screen (desktop-width shell). Routing and "open in new tab" only take over then. */
  visible: boolean;
  userId: string | number | null | undefined;
  /** The page the primary tab is showing. */
  primarySpec: AppTabSpec;
  isDesktop: boolean;
}

export interface MainWindowTabsApi {
  visible: boolean;
  tabs: MainTab[];
  activeUid: string;
  /** The iframe tab in front, or null when the primary tab is. */
  activeFrame: MainTab | null;
  frames: MutableRefObject<Map<string, HTMLIFrameElement>>;
  specOf: (tab: MainTab) => AppTabSpec;
  activate: (uid: string) => void;
  addTab: (spec: AppTabSpec, opts?: { after?: string }) => boolean;
  addTabs: (state: AppPopoutState) => void;
  closeTab: (uid: string) => void;
  closeOthers: (uid: string) => void;
  /** Put `fromUid` where `toUid` is (drag-and-drop's original contract). */
  moveTab: (fromUid: string, toUid: string) => void;
  /** Move a tab to an index in the strip (clamped to its group: pinned or normal). */
  moveTabTo: (uid: string, index: number) => void;
  moveTabBy: (uid: string, delta: number) => void;
  moveTabToEdge: (uid: string, edge: 'start' | 'end') => void;
  setPinned: (uid: string, pinned: boolean) => void;
  duplicate: (uid: string) => void;
  /** To a new window (no target), or to another open window. The primary tab is opened, never moved. */
  moveToWindow: (uid: string, target?: MoveTarget) => Promise<OpenResult>;
  /** Cross-window plumbing for the strip (drag in / out, "Move to ▸" targets). */
  move: TabMoveApi;
  startDrag: (uid: string) => TabDragPayload | null;
  dropForeign: (payload: TabDragPayload, index: number) => void;
  dragOut: (uid: string, nonce: string | null) => void;
  /** Route a shell navigation to the iframe tab in front. False when the primary tab is in front. */
  navigateActive: (tab: string) => boolean;
  handleShortcut: (key: string) => boolean;
}

export function useMainWindowTabs({ host, visible, userId, primarySpec, isDesktop }: UseMainWindowTabsOptions): MainWindowTabsApi {
  const { t } = useTranslation();
  const [strip, setStrip] = useState<StripState>(() => emptyStrip());
  const frames = useRef(new Map<string, HTMLIFrameElement>());
  const stripRef = useRef(strip);
  stripRef.current = strip;
  const primaryRef = useRef(primarySpec);
  primaryRef.current = primarySpec;
  const shown = host && visible;

  const specOf = useCallback((tab: MainTab): AppTabSpec => tab.spec ?? primaryRef.current, []);

  // ------------------------------------------------------------ persistence
  const storageKey = host ? mainTabsStorageKey(userId) : null;
  // The user whose tabs this window is holding. Signing out makes `host` false
  // too, so this is tracked apart from it.
  const hostUser = useRef<string | number | null>(null);
  useEffect(() => {
    const prev = hostUser.current;
    const next = host ? (userId ?? null) : null;
    if (prev === next) return;
    hostUser.current = next;
    if (prev === null) return;
    // Signed out: forget that user's tabs. Someone else signed in: start clean (theirs load below).
    if (userId === null || userId === undefined) clearMainTabs(prev);
    frames.current.clear();
    setStrip(emptyStrip());
  }, [host, userId]);

  useEffect(() => {
    if (!storageKey || stripRef.current.owner === storageKey) return;
    setStrip(fromPersisted(loadMainTabs(userId), storageKey, isDesktop));
  }, [storageKey, userId, isDesktop]);

  useEffect(() => {
    if (!storageKey || strip.owner !== storageKey) return;
    saveMainTabs(userId, toPersisted(strip));
  }, [strip, storageKey, userId]);

  // ------------------------------------------------------------ actions
  const limitReached = useCallback(() => {
    toast({
      variant: 'info',
      title: t('mainTabs.limit_title', 'Tab limit reached'),
      description: t('mainTabs.limit_body', {
        defaultValue: 'You can have up to {{max}} tabs open. Close one to open another.',
        max: MAX_MAIN_TABS,
      }),
    });
  }, [t]);

  const activate = useCallback((uid: string) => {
    setStrip((s) => (s.tabs.some((x) => x.uid === uid) ? { ...s, tabs: mount(s.tabs, uid), active: uid } : s));
  }, []);

  const addTab = useCallback(
    (spec: AppTabSpec, opts: { after?: string } = {}) => {
      // A page id outside the URL-safe set cannot be opened in a frame.
      if (!isValidTabId(spec.tab)) return false;
      if (stripRef.current.tabs.length >= MAX_MAIN_TABS) {
        limitReached();
        return false;
      }
      const tab = frameTab(spec, isDesktop, true);
      setStrip((s) => {
        const next = [...s.tabs];
        const at = opts.after ? next.findIndex((x) => x.uid === opts.after) : -1;
        if (at >= 0) next.splice(at + 1, 0, tab);
        else next.push(tab);
        // A new tab is never pinned, so it lands after the pinned ones.
        return { ...s, tabs: normalizeOrder(next), active: tab.uid };
      });
      return true;
    },
    [isDesktop, limitReached],
  );

  /** "Open in new tab" from anywhere in this window (or from a page in one of its tabs). */
  const addTabs = useCallback(
    (incoming: AppPopoutState) => {
      const room = MAX_MAIN_TABS - stripRef.current.tabs.length;
      if (room <= 0) {
        limitReached();
        return;
      }
      const specs = incoming.tabs.slice(0, room);
      if (specs.length < incoming.tabs.length) limitReached();
      const added = specs.map((spec) => frameTab(spec, isDesktop, false));
      const front = added.find((x) => x.spec && appTabKey(x.spec) === incoming.active) ?? added[0];
      setStrip((s) => ({ ...s, tabs: mount([...s.tabs, ...added], front.uid), active: front.uid }));
    },
    [isDesktop, limitReached],
  );

  const closeTab = useCallback((uid: string) => {
    if (uid === PRIMARY_UID) return;
    frames.current.delete(uid);
    setStrip((s) => {
      const idx = s.tabs.findIndex((x) => x.uid === uid);
      if (idx < 0) return s;
      const rest = s.tabs.filter((_, i) => i !== idx);
      const active = s.active === uid ? (rest[idx] ?? rest[idx - 1] ?? PRIMARY).uid : s.active;
      return { ...s, tabs: mount(rest, active), active };
    });
  }, []);

  const closeOthers = useCallback((uid: string) => {
    setStrip((s) => {
      // Like a browser: pinned tabs survive "close other tabs".
      const keep = s.tabs.filter((x) => x.uid === PRIMARY_UID || x.uid === uid || x.pinned);
      for (const x of s.tabs) if (!keep.includes(x)) frames.current.delete(x.uid);
      const active = keep.some((x) => x.uid === uid) ? uid : PRIMARY_UID;
      return { ...s, tabs: mount(keep, active), active };
    });
  }, []);

  const reorder = useCallback((fn: (tabs: MainTab[]) => MainTab[]) => {
    setStrip((s) => {
      const tabs = fn(s.tabs);
      return tabs === s.tabs ? s : { ...s, tabs };
    });
  }, []);

  const moveTab = useCallback(
    (fromUid: string, toUid: string) => {
      if (fromUid === toUid) return;
      reorder((tabs) => {
        const to = tabs.findIndex((x) => x.uid === toUid);
        return to < 0 ? tabs : moveTabTo(tabs, fromUid, to);
      });
    },
    [reorder],
  );
  const moveTabToIndex = useCallback((uid: string, index: number) => reorder((tabs) => moveTabTo(tabs, uid, index)), [reorder]);
  const moveTabByDelta = useCallback((uid: string, delta: number) => reorder((tabs) => moveTabBy(tabs, uid, delta)), [reorder]);
  const moveTabToEdgeOf = useCallback(
    (uid: string, edge: 'start' | 'end') => reorder((tabs) => moveTabToEdge(tabs, uid, edge)),
    [reorder],
  );
  const setPinned = useCallback((uid: string, pinned: boolean) => reorder((tabs) => setTabPinned(tabs, uid, pinned)), [reorder]);

  const duplicate = useCallback(
    (uid: string) => {
      const tab = stripRef.current.tabs.find((x) => x.uid === uid);
      if (tab) addTab(specOf(tab), { after: uid });
    },
    [addTab, specOf],
  );

  const popupFailed = useCallback(
    (result: OpenResult, retry?: () => void) => {
      const blocked = result.error === 'popup blocked';
      toast({
        variant: 'error',
        title: blocked
          ? t('appPopout.popup_blocked', 'Your browser blocked the new window. Allow pop-ups for this site and try again.')
          : t('appPopout.open_failed', 'Could not open a new window.'),
        // A click is a user gesture, so the popup blocker lets the retry through.
        ...(blocked && retry ? { action: { label: t('tabStrip.open_anyway', 'Open'), onClick: retry } } : {}),
      });
    },
    [t],
  );

  /** Open a tab in a new window; on success an iframe tab leaves this strip (the primary tab never does). */
  const openInNewWindow = useCallback(
    async (uid: string, allowRetry: boolean): Promise<OpenResult> => {
      const tab = stripRef.current.tabs.find((x) => x.uid === uid);
      if (!tab) return { ok: false, error: 'no such tab' };
      const result = await openAppPopout({ tabs: [specOf(tab)] }, 'window', { isDesktop, hostWindow: null });
      if (result.ok) closeTab(uid);
      else popupFailed(result, allowRetry ? () => void openInNewWindow(uid, false) : undefined);
      return result;
    },
    [closeTab, isDesktop, specOf, popupFailed],
  );

  // ------------------------------------------------------------ other windows
  const insertFromWindow = useCallback(
    (spec: AppTabSpec, index: number | undefined, title: string | undefined): MoveInResult => {
      if (!isValidTabId(spec.tab)) return 'invalid';
      if (stripRef.current.tabs.length >= MAX_MAIN_TABS) {
        limitReached();
        return 'full';
      }
      const tab = frameTab(spec, isDesktop, true, title);
      setStrip((s) => {
        const tabs = moveTabTo([...s.tabs, tab], tab.uid, index ?? s.tabs.length + 1);
        return { ...s, tabs, active: tab.uid };
      });
      return 'ok';
    },
    [isDesktop, limitReached],
  );

  const windowId = mainWindowId(isDesktop, isDesktop ? currentDesktopWindowLabel() : null);
  const move = useTabMove({
    windowId,
    enabled: shown,
    isDesktop,
    onMoveIn: insertFromWindow,
    onMovedOut: closeTab,
  });
  useHostAnnouncement(windowId, { enabled: shown && !isDesktop, kind: 'main', title: '' });

  const moveToWindow = useCallback(
    async (uid: string, target?: MoveTarget): Promise<OpenResult> => {
      if (!target) return openInNewWindow(uid, true);
      const tab = stripRef.current.tabs.find((x) => x.uid === uid);
      if (!tab || !tab.spec) return { ok: false, error: 'not movable' };
      const ok = await move.moveToWindow(uid, specOf(tab), tab.title, target.id);
      return ok ? { ok: true } : { ok: false, error: 'not sent' };
    },
    [move, openInNewWindow, specOf],
  );

  const startDrag = useCallback(
    (uid: string) => {
      const tab = stripRef.current.tabs.find((x) => x.uid === uid);
      return tab && tab.spec ? move.startDrag(uid, specOf(tab), tab.title) : null;
    },
    [move, specOf],
  );

  const dropForeign = useCallback(
    (payload: TabDragPayload, index: number) => {
      move.acceptDrop(payload, index);
    },
    [move],
  );

  /** Dropped off the strip: tear it off into its own window — unless another window already took it. */
  const dragOut = useCallback(
    (uid: string, nonce: string | null) => {
      if (uid === PRIMARY_UID) return;
      // Let a late ack from a window that did take the drop arrive first.
      window.setTimeout(() => {
        if (nonce && !move.claimDragOut(nonce)) return;
        void openInNewWindow(uid, true);
      }, 150);
    },
    [move, openInNewWindow],
  );

  /** A page reported where it is (it navigated) and what it is called. */
  const updateTab = useCallback((uid: string, key: string, title?: string) => {
    const spec = parseAppTabKey(key);
    if (!spec) return;
    setStrip((s) => ({
      ...s,
      tabs: s.tabs.map((x) =>
        x.uid === uid && x.spec && (appTabKey(x.spec) !== key || x.title !== title || !x.ready)
          ? { ...x, spec, title, ready: true }
          : x,
      ),
    }));
  }, []);

  const navigateActive = useCallback(
    (tab: string) => {
      if (!shown) return false;
      const cur = stripRef.current.tabs.find((x) => x.uid === stripRef.current.active);
      if (!cur || !cur.spec) return false;
      if (!isValidTabId(tab)) return false;
      const frame = frames.current.get(cur.uid);
      if (cur.ready && frame?.contentWindow) {
        const msg: HostNavigateMessage = { type: 'weavein:navigate', tab };
        frame.contentWindow.postMessage(msg, window.location.origin);
        // Optimistic: the strip and the sidebar highlight follow at once; the
        // page confirms (or corrects, e.g. a legacy id it redirects) with page-state.
        setStrip((s) => ({ ...s, tabs: s.tabs.map((x) => (x.uid === cur.uid ? { ...x, spec: { tab }, title: undefined } : x)) }));
      } else {
        // Still booting, so not listening yet: point the frame at the new page instead.
        const spec = { tab };
        setStrip((s) => ({
          ...s,
          tabs: s.tabs.map((x) =>
            x.uid === cur.uid ? { ...x, spec, title: undefined, src: buildPagePopoutUrl(spec, { desktop: isDesktop }) } : x,
          ),
        }));
      }
      return true;
    },
    [shown, isDesktop],
  );

  const handleShortcut = useCallback(
    (key: string) => {
      const { tabs, active } = stripRef.current;
      if (/^[1-9]$/.test(key)) {
        // With one tab there is nothing to switch to: leave the key to the browser.
        if (tabs.length < 2) return false;
        const n = Number(key);
        const target = n === 9 ? tabs[tabs.length - 1] : tabs[n - 1];
        if (target) activate(target.uid);
        return true;
      }
      // Desktop only: in a browser Cmd/Ctrl+W belongs to the browser.
      if (key === 'w' && isDesktop && active !== PRIMARY_UID) {
        closeTab(active);
        return true;
      }
      if (key === 'move-left' || key === 'move-right') {
        moveTabByDelta(active, key === 'move-left' ? -1 : 1);
        return true;
      }
      return false;
    },
    [activate, closeTab, isDesktop, moveTabByDelta],
  );

  // ------------------------------------------------------------ wiring
  // Pages in our iframes recognise this window as their tab host by this flag.
  useLayoutEffect(() => {
    if (!shown) return;
    setMainTabHostFlag(true);
    return () => setMainTabHostFlag(false);
  }, [shown]);

  // "Open in new tab" in this window adds a tab here.
  useEffect(() => (shown ? registerInAppTabHandler(addTabs) : undefined), [shown, addTabs]);

  useFrameBridge(
    frames,
    (uid, msg) => {
      if (msg.type === 'weavein:page-state') updateTab(uid, msg.key, msg.title);
      else if (msg.type === 'weavein:open') addTabs(stateFromKeys(msg.tabs, msg.active));
      else if (msg.type === 'weavein:shortcut') handleShortcut(msg.key);
    },
    undefined,
    shown,
  );

  useEffect(() => {
    if (!shown) return;
    const onKey = (e: KeyboardEvent) => {
      const key = tabShortcutFromEvent(e);
      if (key && handleShortcut(key)) e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [shown, handleShortcut]);

  const activeTab = strip.tabs.find((x) => x.uid === strip.active) ?? PRIMARY;
  const activeFrame = shown && activeTab.spec ? activeTab : null;

  return {
    visible: shown,
    tabs: strip.tabs,
    activeUid: shown ? strip.active : PRIMARY_UID,
    activeFrame,
    frames,
    specOf,
    activate,
    addTab,
    addTabs,
    closeTab,
    closeOthers,
    moveTab,
    moveTabTo: moveTabToIndex,
    moveTabBy: moveTabByDelta,
    moveTabToEdge: moveTabToEdgeOf,
    setPinned,
    duplicate,
    moveToWindow,
    move,
    startDrag,
    dropForeign,
    dragOut,
    navigateActive,
    handleShortcut,
  };
}

export default useMainWindowTabs;
